import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useFormFunnelTracking } from "../hooks/useFormFunnelTracking";
import { useCallback } from "react";
import Link from "next/link";
import { FunnelButton } from "../FunnelButton";
import { toRelativePath } from "../utils/urlUtils";
import { getUrls } from "@portal/app/_common/lib/urls";
import { useFormFunnelHeadingLevel } from "../FormFunnelHeadingContext";

export interface ProgramUnavailableResultProps {
  programName?: string;
  searchUrl?: string;
  sessionId?: string;
  similarPrograms?: {
    id: string;
    name: string;
    providerName: string;
    providerSlug: string;
    slug: string;
  }[];
}

export const ProgramUnavailableResult = ({
  programName,
  searchUrl = "/explore",
  sessionId,
  similarPrograms = [],
}: ProgramUnavailableResultProps) => {
  const headingLevel = useFormFunnelHeadingLevel();
  const tracking = useFormFunnelTracking();
  const hasSimilarPrograms = similarPrograms.length > 0;

  const handleSearchClick = useCallback(() => {
    tracking.trackEvent("Form Funnel - Unavailable - Search Clicked", {
      destinationUrl: searchUrl,
      sessionId,
    });
  }, [tracking, searchUrl, sessionId]);

  const handleProgramClick = useCallback(
    (programId: string) => {
      tracking.trackEvent("Form Funnel - Unavailable - Similar Program Clicked", {
        programId,
        sessionId,
      });
    },
    [tracking, sessionId]
  );

  return (
    <Box
      data-testid="program-unavailable-result"
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 3,
        p: 4,
        textAlign: "center",
        maxWidth: 600,
        mx: "auto",
        border: "1px solid #eee",
        borderRadius: 2,
        boxShadow: 1,
      }}
    >
      <Typography variant="h4" component={headingLevel}>
        This program isn&apos;t available in your area
      </Typography>

      <Typography variant="body1" color="text.secondary">
        {programName
          ? `Unfortunately, ${programName} is not available based on your location.`
          : "Unfortunately, this program is not available based on your location."}
        {hasSimilarPrograms
          ? " However, we found some similar programs you might like."
          : " Try browsing all programs to find options near you."}
      </Typography>

      {similarPrograms.length > 0 && (
        <Box sx={{ width: "100%", textAlign: "left", mt: 2 }}>
          <Typography variant="h6" gutterBottom>
            You might be interested in:
          </Typography>
          <Box component="ul" sx={{ pl: 2 }}>
            {similarPrograms.map((program) => (
              <Box component="li" key={program.id} sx={{ mb: 1 }}>
                <Link
                  href={getUrls().ProviderProgramStorefront(program.providerSlug, program.slug)}
                  onClick={() => handleProgramClick(program.id)}
                  style={{ textDecoration: "none", color: "inherit" }}
                >
                  <Typography
                    variant="body1"
                    color="primary"
                    sx={{ textDecoration: "underline", cursor: "pointer" }}
                  >
                    {program.providerName} - {program.name}
                  </Typography>
                </Link>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      <Box
        sx={{
          mt: 2,
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Typography variant="body2" sx={{ mb: 2 }}>
          {hasSimilarPrograms ? "Or browse all programs:" : "Browse all programs:"}
        </Typography>
        <Link href={toRelativePath(searchUrl)} passHref legacyBehavior>
          <FunnelButton
            component="a"
            onClick={handleSearchClick}
            data-testid="program-unavailable-search-button"
          >
            View All Programs
          </FunnelButton>
        </Link>
      </Box>
    </Box>
  );
};
